import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ShoppingCart, Star, Zap } from 'lucide-react';
import { onPublicImageError } from '../lib/publicImageFallback';
import { NAVBAR_SCROLL_OFFSET } from '../lib/hashSectionNav';

import { THERMACUBE_CHECKOUT_URL } from '../constants/storeUrls';

const CHECKOUT_URL = THERMACUBE_CHECKOUT_URL;

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_SCROLL_OFFSET;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const handleOrder = (e) => {
    e.preventDefault();
    if (CHECKOUT_URL) {
      window.open(CHECKOUT_URL, '_blank');
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => scrollTo('contact'), 150);
      } else {
        scrollTo('contact');
      }
    }
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative bg-[#0c0c0d]/80 border border-white/[0.06] hover:border-white/15 transition-all duration-500 overflow-hidden shadow-[0_2px_40px_-16px_rgba(0,0,0,0.5)]"
    >
      {/* Discount Badge */}
      {discount > 0 && (
        <div className="absolute top-4 left-4 z-10 bg-white text-neutral-900 font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 border border-white/10">
          {discount}% off
        </div>
      )}

      <Link
        to={`/products/${product.id}`}
        className="block aspect-square overflow-hidden bg-zinc-900 relative focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9fd4c9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c0c0d]"
        aria-label={`${product.name} — product details`}
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          onError={onPublicImageError}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      </Link>

      {/* Content */}
      <div className="p-6 space-y-4">
        <span className="font-mono text-xs uppercase tracking-[0.16em] text-zinc-500">
          {product.category ? product.category.replace(/-/g, ' ') : 'Hot Cold Therapy'}
        </span>

        <h3 className="font-heading text-2xl font-semibold uppercase tracking-tight group-hover:text-[#9fd4c9] transition-colors duration-300">
          {product.name}
        </h3>

        <p className="text-zinc-400 text-sm line-clamp-2">
          {product.short_description}
        </p>

        {/* Rating */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${i < Math.floor(product.rating) ? 'fill-[#8fb8ae] text-[#8fb8ae]' : 'text-zinc-700'}`}
              />
            ))}
          </div>
          <span className="text-zinc-400 text-sm">({product.reviews_count} reviews)</span>
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
          <div className="flex items-baseline gap-2">
            <span className="font-heading text-3xl font-semibold">₹{product.price.toLocaleString('en-IN')}</span>
            {product.original_price && (
              <span className="text-zinc-500 line-through text-sm">₹{product.original_price.toLocaleString('en-IN')}</span>
            )}
          </div>

          <button
            onClick={handleOrder}
            data-testid={`add-to-cart-${product.id}`}
            className="flex items-center gap-2 px-5 py-3 bg-white text-neutral-900 font-heading text-xs font-semibold uppercase tracking-[0.14em] hover:bg-zinc-200 transition-colors duration-300 border border-white/10"
          >
            <ShoppingCart className="w-4 h-4" />
            <span>
              {CHECKOUT_URL ? 'Buy Now' : 'Pre-Order'}
            </span>
          </button>
        </div>

        {/* Pre-order notice */}
        {!CHECKOUT_URL && (
          <p className="text-zinc-500 text-xs text-center">
            Click Pre-Order to reach us directly — we'll confirm your order within 24 hours.
          </p>
        )}
      </div>
    </div>
  );
};
